/**
 * Fluxo de teleconsulta a partir do inbox WhatsApp:
 * resolve agendamento do dia, habilita sala e devolve link do paciente.
 */
import axios from '../config/axios';
import { getApiUrl } from '../config/environment';
import {
  enableTeleconsult,
  getTeleconsultStatus,
  type TeleconsultStatus,
} from '../services/teleconsultService';

const ELIGIBLE_STATUSES = new Set([
  'scheduled',
  'confirmed',
  'waiting',
  'in_progress',
]);

export interface InboxAppointmentCandidate {
  id: string;
  appointment_date: string;
  start_time?: string;
  status: string;
}

export interface InboxTeleconsultStartResult {
  appointmentId: string;
  patientUrl: string;
  patientTokenExpiresAt: string | null;
  createdAppointment: boolean;
}

function localDateYmd(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function localTimeHm(): string {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

function pickTodayAppointment(
  appointments: InboxAppointmentCandidate[] | undefined
): InboxAppointmentCandidate | null {
  if (!appointments?.length) return null;
  const today = localDateYmd();
  const todayEligible = appointments.filter(
    (a) => a.appointment_date === today && ELIGIBLE_STATUSES.has(a.status)
  );
  if (!todayEligible.length) return null;

  const priority = ['in_progress', 'waiting', 'confirmed', 'scheduled'];
  for (const st of priority) {
    const found = todayEligible.find((a) => a.status === st);
    if (found) return found;
  }
  return todayEligible[0];
}

async function createTodayTeleconsultAppointment(patientId: string): Promise<string> {
  const { data: res } = await axios.post(getApiUrl('/clinic/healthcare-panel/quick-schedule'), {
    patient_id: patientId,
    appointment_date: localDateYmd(),
    start_time: localTimeHm(),
    duration_minutes: 60,
    appointment_type: 'consultation',
    title: 'Teleconsulta via WhatsApp',
    notes: 'Iniciada pelo inbox — teleconsulta imediata',
  });
  if (!res?.success || !res?.data?.id) {
    throw new Error(res?.error || 'Não foi possível criar o agendamento para teleconsulta.');
  }
  return res.data.id as string;
}

async function ensureTeleconsultEnabled(appointmentId: string): Promise<TeleconsultStatus> {
  let status = await getTeleconsultStatus(appointmentId);
  if (status.enabled && status.patientUrl) {
    return status;
  }
  try {
    const enabled = await enableTeleconsult(appointmentId);
    return {
      appointmentId: enabled.appointmentId,
      enabled: true,
      roomName: enabled.roomName,
      patientUrl: enabled.patientUrl,
      patientTokenExpiresAt: enabled.patientTokenExpiresAt,
      startedAt: status.startedAt,
      endedAt: status.endedAt,
    };
  } catch (err: unknown) {
    const msg =
      (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
      (err as Error)?.message ||
      '';
    if (!/expirou|expirad/i.test(msg)) {
      throw err;
    }
    throw new Error(
      'O horário do agendamento de hoje já não permite teleconsulta. Tente novamente — será criado um novo horário.'
    );
  }
}

/**
 * Prepara teleconsulta para o paciente vinculado: usa consulta de hoje ou cria uma nova.
 */
export async function startInboxTeleconsult(params: {
  patientId: string;
  upcomingAppointments?: InboxAppointmentCandidate[];
}): Promise<InboxTeleconsultStartResult> {
  let appointmentId = pickTodayAppointment(params.upcomingAppointments)?.id;
  let createdAppointment = false;

  if (appointmentId) {
    try {
      const tele = await ensureTeleconsultEnabled(appointmentId);
      if (tele.patientUrl) {
        return {
          appointmentId,
          patientUrl: tele.patientUrl,
          patientTokenExpiresAt: tele.patientTokenExpiresAt,
          createdAppointment: false,
        };
      }
    } catch (e) {
      const msg = (e as Error).message || '';
      if (!/expirou|expirad|horário|prazo/i.test(msg)) {
        throw e;
      }
      appointmentId = undefined;
    }
  }

  appointmentId = await createTodayTeleconsultAppointment(params.patientId);
  createdAppointment = true;
  const tele = await ensureTeleconsultEnabled(appointmentId);
  if (!tele.patientUrl) {
    throw new Error('Teleconsulta habilitada, mas o link do paciente não foi gerado.');
  }

  return {
    appointmentId,
    patientUrl: tele.patientUrl,
    patientTokenExpiresAt: tele.patientTokenExpiresAt,
    createdAppointment,
  };
}

export function buildTeleconsultWhatsAppMessage(patientName: string, patientUrl: string): string {
  const name = patientName?.trim() || 'paciente';
  return (
    `Olá, ${name}! 👋\n\n` +
    `Segue o link para entrar na *teleconsulta*:\n\n` +
    `${patientUrl}\n\n` +
    `Qualquer dúvida, estamos à disposição.`
  );
}

export function openProfessionalTeleconsultRoom(appointmentId: string): void {
  window.open(`/clinic/teleconsulta/${appointmentId}`, '_blank', 'noopener,noreferrer');
}
